import { z } from 'zod';
import { Id, Instant, LocaleCode, Version } from '../primitives.js';
// ONTWERP.md §4.1 `member`: een persoon in het huishouden (met of zonder login).
// Nooit fysiek verwijderd; "definitief verwijderen" = anonimiseren via rol `erasure`.
export const MemberKind = z.enum(['adult', 'child', 'helper', 'other']);
/** Okabe–Ito-palet (beslissing #28), CVD-veilig. */
export const ColorKey = z.enum([
    'orange',
    'skyblue',
    'bluishgreen',
    'yellow',
    'blue',
    'vermillion',
    'reddishpurple',
    'black',
]);
/**
 * Volgorde waarin een lid een vrije kleur krijgt (DESIGN.md §3.2: de zes best onderscheidbare
 * tinten eerst; gelijk aan ui-core MEMBER_COLOR_DEFAULT_ORDER).
 */
export const COLOR_KEY_ORDER = [
    'blue',
    'orange',
    'bluishgreen',
    'reddishpurple',
    'skyblue',
    'vermillion',
    'yellow',
    'black',
];
function isColorKey(v) {
    return COLOR_KEY_ORDER.indexOf(v) >= 0;
}
/**
 * Kleur voor een nieuw lid (oprichter 1 okt: "iedereen moet een aparte kleur hebben"): de gevraagde
 * kleur als die nog vrij is, anders de eerste vrije in COLOR_KEY_ORDER. Zijn alle acht kleuren al
 * bezet, dan blijft de gevraagde staan (een negende lid deelt onvermijdelijk een kleur).
 */
export function freeColorKey(requested, used) {
    const want = requested && isColorKey(requested) ? requested : null;
    if (want && used.indexOf(want) < 0)
        return want;
    return COLOR_KEY_ORDER.find((c) => used.indexOf(c) < 0) ?? want ?? 'blue';
}
/**
 * Leden die dezelfde kleur delen (of een onbekende sleutel hebben, die als grijs zou tonen):
 * id → de kleur die ze krijgen. Wie eerder in `members` staat (gezinsvolgorde) houdt zijn kleur;
 * de volgende krijgt de eerste vrije. Lege map = alles al verschillend. Deterministisch, zodat app
 * en bord dezelfde kleuren tonen, ook voordat de reparatie is bewaard.
 */
export function duplicateColorFixes(members) {
    const fixes = new Map();
    const used = [];
    const later = [];
    for (const m of members) {
        if (isColorKey(m.color_key) && used.indexOf(m.color_key) < 0)
            used.push(m.color_key);
        else
            later.push(m);
    }
    for (const m of later) {
        const next = COLOR_KEY_ORDER.find((c) => used.indexOf(c) < 0);
        if (!next)
            break;
        used.push(next);
        fixes.set(m.id, next);
    }
    return fixes;
}
/**
 * 0057 (oprichter 1 okt): initialen achter de afspraak ("Sohbet · A", "OA" voor Omer Asaf).
 * Eén tot drie letters, opgeslagen in hoofdletters. Spaties, punten en cijfers vallen weg.
 */
export const MemberInitials = z
    .string()
    .transform((s) => s.replace(/[\s.\-']/g, '').toUpperCase())
    .pipe(z.string().regex(/^\p{L}{1,3}$/u, '1 tot 3 letters'));
/**
 * Initialen uit een naam: de eerste letter van elk woord, hooguit drie, in hoofdletters
 * ("Omer Asaf" → "OA", "Anne" → "A"). Leeg als er geen letter in de naam staat. Gelijk aan
 * member_initials_from (0057) voor gewone namen.
 */
export function initialsFromName(name) {
    let out = '';
    for (const word of (name ?? '').split(/[^\p{L}]+/u)) {
        const first = Array.from(word)[0];
        if (first)
            out += first.toUpperCase();
        if (Array.from(out).length >= 3)
            break;
    }
    return Array.from(out).slice(0, 3).join('');
}
export const AgeBand = z.enum(['0-5', '6-9', '10-12', '13-15', '16+']);
export const ReadingLevel = z.enum(['icons', 'simple', 'full']);
export const Member = z.object({
    id: Id,
    household_id: Id,
    display_name: z.string().trim().min(1).max(40),
    kind: MemberKind,
    color_key: ColorKey,
    avatar_media_id: Id.nullable().default(null),
    avatar_icon: z.string().max(64).nullable().default(null),
    age_band: AgeBand.nullable().default(null),
    birth_year: z.number().int().min(1900).max(2100).nullable().default(null),
    reading_level: ReadingLevel.default('simple'),
    points_enabled: z.boolean().default(true),
    sort_order: z.number().int().default(0),
    show_on_kiosks: z.boolean().default(true),
    /** Golf 3 (0033_talen): eigen taal; null = "Zoals het gezin". */
    locale: LocaleCode.nullable().default(null),
    /** 0057: initialen achter de afspraak; null = uit de naam. */
    initials: z.string().min(1).max(3).nullable().default(null),
    balance_cache: z.number().int().default(0),
    reserved_cache: z.number().int().nonnegative().default(0),
    version: Version,
    created_at: Instant,
    updated_at: Instant,
    archived_at: Instant.nullable().default(null),
    anonymised_at: Instant.nullable().default(null),
});
export const CreateMember = Member.pick({
    display_name: true,
    kind: true,
    color_key: true,
    avatar_icon: true,
    age_band: true,
    birth_year: true,
    reading_level: true,
    points_enabled: true,
    show_on_kiosks: true,
})
    .partial({
    avatar_icon: true,
    age_band: true,
    birth_year: true,
    reading_level: true,
    points_enabled: true,
    show_on_kiosks: true,
})
    .refine((m) => m.kind !== 'other' || m.points_enabled !== true, {
    message: 'pseudo-lid "Anders" krijgt nooit punten',
    path: ['points_enabled'],
});
/** Beschikbaar saldo = balance − reserved (ONTWERP.md §4.4 reserveringsmodel). */
export function availablePoints(member) {
    return Math.max(0, member.balance_cache - member.reserved_cache);
}
//# sourceMappingURL=member.js.map